import type { components } from "@/api/schema";

type ActivityEvent = components["schemas"]["ActivityEvent"];

/** Deep link to one order's rows in the global Audit log (admin-only page). */
export interface OrderAuditNavigation {
  to: "/audit-log";
  search: {
    preset: "30d";
    module: "orders";
    target_type: "order";
    target_id: string;
    offset: number;
    event_id?: number;
  };
}

/** "View all in Audit log" — every audit row for this order, no single event preselected. */
export function auditNavigationForOrder(orderId: string): OrderAuditNavigation {
  return {
    to: "/audit-log",
    search: {
      preset: "30d",
      module: "orders",
      target_type: "order",
      target_id: orderId,
      offset: 0,
    },
  };
}

/** One activity row's `audit_event_id` — same as auditNavigationForOrder, plus the row preselected. */
export function auditNavigationForEvent(orderId: string, eventId: number): OrderAuditNavigation {
  return {
    to: "/audit-log",
    search: {
      preset: "30d",
      module: "orders",
      target_type: "order",
      target_id: orderId,
      offset: 0,
      event_id: eventId,
    },
  };
}

/** Half-width of the date range built around an event's own timestamp. */
const CONTEXT_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface StockAuditNavigation {
  to: "/audit-log";
  search: {
    preset: "custom";
    date_from: string;
    date_to: string;
    offset: number;
    module?: "inventory";
    search?: string;
  };
}

/**
 * Copied (not imported — features cannot import each other, CLAUDE.md rule
 * 2) from src/features/inventory/lib/audit-log-link.ts's
 * auditLogNavigationFor. Adapted to ActivityEvent, which has no `context`
 * field: a legacy stock_audit_log row (stock_audit_id set, no
 * audit_event_id) has no exact global-ledger id either, so this builds the
 * same date-window + SKU fallback search instead of an exact-event link.
 */
export function auditLogNavigationFor(
  sku: string,
  event: Pick<ActivityEvent, "at">,
): StockAuditNavigation {
  const at = event.at ? new Date(event.at) : new Date();
  const anchor = Number.isNaN(at.getTime()) ? Date.now() : at.getTime();
  const date_from = new Date(anchor - CONTEXT_WINDOW_MS).toISOString();
  const date_to = new Date(anchor + CONTEXT_WINDOW_MS).toISOString();

  return {
    to: "/audit-log",
    search: {
      preset: "custom",
      date_from,
      date_to,
      offset: 0,
      ...(sku && { module: "inventory", search: sku }),
    },
  };
}
